import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {LinkNavigator} from '../../../../../../libs/shared/ui/src/components/navigation/navigationContext';
import {useLinkNavigation} from '../../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../../../../models/url/models/types';
import {buildSettingsPath} from '../../../../../models/url/settingsUrlHelpers';
import {SettingsChatBotsEditorTabsEnum} from '../../../../../models/url/settingsUrlModel';
import {settingsChatBotLearnMoreLink} from '../../../../../settings/inboxes/channels/chat/settingsChatbotHelpers';
import {getHasPermission} from '../../../../../store/data/access/accessSelectors';

/*
 * Props.
 */

interface ActivityDecisionTreePillProps {
  decisionTreeId: number;
  decisionTreeName: string;
  decisionTreeNamespace: string;
}

interface StyledDecisionTreePillDivProps {
  $isOpen: boolean;
}

/*
 * Style.
 */

const StyledDecisionTreePillDiv = styled.div<StyledDecisionTreePillDivProps>`
  padding: 0 4px 1px 3px;
  max-width: 250px;

  background: ${(p) => (p.$isOpen ? p.theme.greys.shade30 : p.theme.greys.shade10)};
  border: 1px solid ${(p) => p.theme.greys.shade60};
  border-radius: 100px;

  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};

  ${ellipsis()};

  &:hover {
    background: ${(p) => p.theme.greys.shade30};
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledGearIcon = styled(Icon)`
  display: inline-block;
  vertical-align: middle;
  margin: 0 4px 0 0;
`;

const StyledTreeNameDiv = styled.div`
  display: inline-block;
  padding-top: 1px;
`;

const StyledLinkIcon = styled(Icon)`
  display: inline-block;
  margin-bottom: -3px;
`;

const StyledLearnMoreDiv = styled.div`
  height: 100%;

  & > ${StyledLinkIcon} {
    color: ${(p) => p.theme.greys.shade60};
  }

  &:hover > ${StyledLinkIcon} {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

const StyledPillContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  & ${StyledTreeNameDiv} {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StyledTooltipContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/*
 * Component.
 */

export const ActivityDecisionTreePill: FC<ActivityDecisionTreePillProps> = (props) => {
  const {decisionTreeNamespace} = props;

  const {navigateToLink} = useLinkNavigation();

  // Access to the Chat Bots is shared with INBOX_UPDATE.
  const hasPermission = useAppSelector(getHasPermission);
  const hasBuilderAccess = hasPermission(decisionTreeNamespace, PermissionsEnum.INBOX_UPDATE);

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderPill(isOpen, props)}
      renderModal={(popoverProps) => renderDropdown(popoverProps, props, navigateToLink, hasBuilderAccess)}
    />
  );
};

/*
 * Helpers.
 */

function renderPill(isOpen: boolean, props: ActivityDecisionTreePillProps) {
  const {decisionTreeName} = props;

  return (
    <StyledDecisionTreePillDiv $isOpen={isOpen}>
      <TooltipCoordinator render={() => renderTooltipContent(decisionTreeName)}>
        {renderPillContent(decisionTreeName)}
      </TooltipCoordinator>
    </StyledDecisionTreePillDiv>
  );
}

function renderPillContent(decisionTreeName: string) {
  return (
    <StyledPillContentDiv>
      <StyledGearIcon name="gearFilledTiny" />
      <StyledTreeNameDiv>{decisionTreeName}</StyledTreeNameDiv>
    </StyledPillContentDiv>
  );
}

function renderTooltipContent(decisionTreeName: string) {
  return (
    <StyledTooltipContentDiv>
      <StyledGearIcon name="gearFilledTiny" />
      <StyledTreeNameDiv>{decisionTreeName}</StyledTreeNameDiv>
    </StyledTooltipContentDiv>
  );
}

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: ActivityDecisionTreePillProps,
  navigateToLink: LinkNavigator,
  hasBuilderAccess: boolean,
) {
  const {decisionTreeNamespace, decisionTreeId} = props;

  const onNavigateToBotClick = () =>
    navigateToLink(
      buildSettingsPath({
        namespace: decisionTreeNamespace,
        rootPage: SettingsRootPagesEnum.CHAT_BOTS,
        resourcePage: SettingsResourcePagesEnum.EDIT,
        decisionTreeId,
        tabPage: SettingsChatBotsEditorTabsEnum.BUILDER,
      }),
    );

  const onLearnMoreClick = () => navigateToLink(settingsChatBotLearnMoreLink);

  return (
    <Dropdown {...popoverProps} maxWidth={200}>
      <DropdownList {...popoverProps}>
        <SimpleDropdownItem isDisabled={!hasBuilderAccess} onSelect={onNavigateToBotClick}>
          <Trans id="activity-decisionTree-pill-edit">Edit chatbot</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItem onSelect={onLearnMoreClick}>
          <Trans id="activity-decisionTree-pill-help">
            <StyledLearnMoreDiv>
              Learn more about chatbots <StyledLinkIcon name="externalLinkSmall" />
            </StyledLearnMoreDiv>
          </Trans>
        </SimpleDropdownItem>
      </DropdownList>
    </Dropdown>
  );
}
